import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Eye, CheckCircle, XCircle, Clock, Send, AlertDialog } from 'lucide-react';

const STATUS_LABELS = { draft: 'Rascunho', pending: 'Pendente', published: 'Publicado', rejected: 'Rejeitado' };
const STATUS_COLORS = { draft: 'badge-gray', pending: 'badge-gold', published: 'badge-green', rejected: 'badge-red' };

export default function Articles() {
  const { user, isEditor, isAdmin } = useAuth();
  const { t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const fetchArticles = () => {
    const params = filter ? `?status=${filter}` : '';
    api.get(`/articles/all${params}`).then(r => setArticles(r.data.articles || [])).finally(() => setLoading(false));
  };

  useEffect(() => { fetchArticles(); }, [filter]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/articles/${id}`);
      toast.success('Artigo eliminado');
      setDeleteId(null);
      fetchArticles();
    } catch { toast.error('Erro ao eliminar artigo'); }
  };

  const handleStatus = async (id, status) => {
    try {
      await api.patch(`/articles/${id}/status`, { status });
      toast.success(`Artigo: ${STATUS_LABELS[status]}`);
      fetchArticles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao atualizar estado'); }
  };

  return (
    <div data-testid="articles-management">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('backoffice.articles')}</h1>
        <Link to="/admin/artigos/novo" data-testid="create-article-btn"
          className="flex items-center gap-2 px-4 py-2 bg-[#CC0000] text-white text-sm font-medium rounded-md hover:bg-[#A30000] transition-colors">
          <Plus size={14} /> {t('backoffice.new_article')}
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {['', 'draft', 'pending', 'published', 'rejected'].map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s || 'all'}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filter === s ? 'bg-[#CC0000] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {s ? STATUS_LABELS[s] : 'Todos'}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[...Array(5)].map(i => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : articles.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <p className="mb-3">Nenhum artigo encontrado.</p>
            <Link to="/admin/artigos/novo" className="text-[#CC0000] hover:underline text-sm">Criar primeiro artigo</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Título</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Autor</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Data</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {articles.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50 transition-colors" data-testid={`article-${a.id}`}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900 line-clamp-1">{a.title}</p>
                      {a.category_name && <p className="text-xs text-gray-400">{a.category_name}</p>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-gray-600">{a.author_name}</span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={`badge ${STATUS_COLORS[a.status]}`}>{STATUS_LABELS[a.status] || a.status}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-gray-400">
                      {new Date(a.created_at).toLocaleDateString('pt')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/noticias/${a.id}`} target="_blank"
                          className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors" title="Ver">
                          <Eye size={14} />
                        </Link>
                        <Link to={`/admin/artigos/${a.id}/editar`} data-testid={`edit-${a.id}`}
                          className="p-1.5 text-gray-400 hover:text-[#CC0000] rounded hover:bg-red-50 transition-colors" title="Editar">
                          <Edit2 size={14} />
                        </Link>
                        {isEditor() && a.status === 'pending' && (
                          <>
                            <button onClick={() => handleStatus(a.id, 'published')} title="Publicar"
                              className="p-1.5 text-gray-400 hover:text-green-600 rounded hover:bg-green-50 transition-colors">
                              <CheckCircle size={14} />
                            </button>
                            <button onClick={() => handleStatus(a.id, 'rejected')} title="Rejeitar"
                              className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors">
                              <XCircle size={14} />
                            </button>
                          </>
                        )}
                        {a.status === 'draft' && (
                          <button onClick={() => handleStatus(a.id, 'pending')} title="Submeter para revisão"
                            className="p-1.5 text-gray-400 hover:text-orange-600 rounded hover:bg-orange-50 transition-colors">
                            <Send size={14} />
                          </button>
                        )}
                        {(isAdmin() || a.author_id === user?.id) && (
                          <button onClick={() => setDeleteId(a.id)} data-testid={`delete-${a.id}`}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors" title="Eliminar">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="font-bold text-gray-900 mb-2">Confirmar Eliminação</h3>
            <p className="text-sm text-gray-500 mb-5">{t('common.confirm_delete')}</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">{t('common.cancel')}</button>
              <button onClick={() => handleDelete(deleteId)} data-testid="confirm-delete"
                className="flex-1 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700">{t('common.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
